import { createUniqueId } from '../../../utils/common-util';
import { NOTIFICATION_MESSAGES } from '../../../utils/notification-messages';
import { toastWarn } from '../../../utils/toasts';
import { validateClipboardInput } from '../../../utils/validations';

export function dateComparator(a, b) {
   function parseDate(dateStr) {
      const monthMap = {
         JAN: 0,
         FEB: 1,
         MAR: 2,
         APR: 3,
         MAY: 4,
         JUN: 5,
         JUL: 6,
         AUG: 7,
         SEP: 8,
         OCT: 9,
         NOV: 10,
         DEC: 11,
      };

      const day = parseInt(dateStr.substring(0, 2), 10);
      const month = monthMap[dateStr.substring(2, 5)];
      const year = parseInt(dateStr.substring(5, 7), 10) + 2000; // Adjust year for 2000s
      const timeStr = dateStr.substring(8);

      if (timeStr) {
         const [hours, minutes] = timeStr.split(':').map(Number);
         return new Date(year, month, day, hours, minutes);
      }

      return new Date(year, month, day);
   }

   const dateA = parseDate(a);
   const dateB = parseDate(b);

   return dateA - dateB;
}

export function handleKeyboardNavigation(updateSelectedRows) {
   return function onCellKeyDown(params) {
      const key = params.event.key;
      const currentRowIndex = params.node.rowIndex;
      let nextRowIndex;

      if (key === 'ArrowDown') {
         nextRowIndex = currentRowIndex + 1;
      } else if (key === 'ArrowUp') {
         nextRowIndex = currentRowIndex - 1;
      }

      if (nextRowIndex !== undefined) {
         const displayedRowCount = params.api.getDisplayedRowCount();
         if (nextRowIndex >= 0 && nextRowIndex < displayedRowCount) {
            params.api.forEachNode((node) => node.setSelected(false));

            const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);
            if (nextRowNode) {
               nextRowNode.setSelected(true);
               params.api.ensureIndexVisible(nextRowIndex);
            }

            params.event.preventDefault();
         }

         updateSelectedRows();
      }
   };
}

export function createDragToSelectHandlers({
   isDraggingRef,
   startRowRef,
   updateSelectedRows,
   gridApiRef,
}) {
   function selectRowsInRange(start, end) {
      if (!gridApiRef) return;
      const min = Math.min(start, end);
      const max = Math.max(start, end);

      gridApiRef.forEachNode((node) => {
         node.setSelected(node.rowIndex >= min && node.rowIndex <= max);
      });
   }

   function onCellMouseDown(params) {
      isDraggingRef.current = true;
      startRowRef.current = params.node.rowIndex;

      document.addEventListener('mouseup', onMouseUp);
      params.event.preventDefault();
   }

   function onCellMouseOver(params) {
      if (isDraggingRef.current && startRowRef.current !== null) {
         const currentRowIndex = params.node.rowIndex;
         selectRowsInRange(startRowRef.current, currentRowIndex);
      }
   }

   function onMouseUp() {
      isDraggingRef.current = false;
      startRowRef.current = null;
      document.removeEventListener('mouseup', onMouseUp);
      updateSelectedRows();
   }

   return {
      onCellMouseDown,
      onCellMouseOver,
      onMouseUp,
   };
}

export function createFirstDataRenderedHandler(autoSizeColumns) {
   return function onFirstDataRendered(params) {
      if (autoSizeColumns) {
         const allColumnIds = params.columnApi
            .getAllColumns()
            .map((column) => column.colId);

         params.columnApi.autoSizeColumns(allColumnIds);
      }
   };
}

export function createRowClickHandler(updateSelectedRows, lastRowClickedRef) {
   return function handleRowClick(params) {
      const event = params.event;
      const rowIndex = params.node.rowIndex;

      const ctrlPressed = event.ctrlKey || event.metaKey;
      const shiftPressed = event.shiftKey;

      if (shiftPressed) {
         if (lastRowClickedRef.current !== null) {
            const min = Math.min(lastRowClickedRef.current, rowIndex);
            const max = Math.max(lastRowClickedRef.current, rowIndex);

            params.api.forEachNode((node) => {
               if (node.rowIndex >= min && node.rowIndex <= max) {
                  node.setSelected(true);
               }
            });
         }
         lastRowClickedRef.current = rowIndex;
      } else if (ctrlPressed) {
         const isSelected = params.node.isSelected();
         params.node.setSelected(!isSelected);
         lastRowClickedRef.current = rowIndex;
      } else {
         params.api.forEachNode((node) => node.setSelected(false));
         params.node.setSelected(true);
         lastRowClickedRef.current = rowIndex;
      }

      updateSelectedRows();
   };
}

export function navigateToNextCell(params) {
   const previousCell = params.previousCellPosition;
   const nextCell = params.nextCellPosition;

   if (previousCell?.rowIndex === 0 && params.key === 'ArrowUp') {
      return null;
   }

   return nextCell;
}

export function addRowAboveSelected(gridApiRef, onAddNewRow) {
   if (!gridApiRef || !gridApiRef.current) {
      console.error('Grid API reference is not provided or invalid.');
      return;
   }

   const api = gridApiRef.current;
   const selectedNodes = api.getSelectedNodes();

   const selectedNode = selectedNodes[0];
   let newRows = selectedNodes.map(() => ({
      id: createUniqueId(),
      isNewRow: true,
   }));

   if (newRows.length === 0) {
      newRows = [{ id: createUniqueId(), isNewRow: true }];
   }

   api.applyTransaction({
      add: newRows,
      addIndex: selectedNode?.rowIndex || 0,
   });

   onAddNewRow?.(newRows, gridApiRef.current);
}

export function addTemplateAboveSelected(gridApiRef, onTemplate) {
   if (!gridApiRef || !gridApiRef.current) {
      console.error('Grid API reference is not provided or invalid.');
      return;
   }

   const api = gridApiRef.current;
   const selectedNodes = api.getSelectedNodes();

   if (selectedNodes.length === 0) {
      toastWarn(NOTIFICATION_MESSAGES.ROW_NOT_SELECTED);
      return;
   }

   const rowIndex = selectedNodes[0]?.rowIndex;
   const newRows = selectedNodes.map((node) => ({
      ...node.data,
      id: createUniqueId(),
      isNewRow: true,
   }));

   api.applyTransaction({
      add: newRows,
      addIndex: rowIndex,
   });

   onTemplate?.(newRows, gridApiRef.current);
}

async function deleteSelectedRows(
   gridApiRef,
   onDeleteChanges,
   preventDefaultRowDeletion
) {
   if (!gridApiRef || !gridApiRef.current) {
      console.error('Grid API reference is not provided or invalid.');
      return;
   }

   const api = gridApiRef.current;
   const selectedNodes = api.getSelectedNodes();

   if (selectedNodes.length === 0) {
      toastWarn(NOTIFICATION_MESSAGES.ROW_NOT_SELECTED);
      return;
   }

   const selectedRowIndices = selectedNodes.map((node) => node.rowIndex);
   const minIndex = Math.min(...selectedRowIndices);
   const maxIndex = Math.max(...selectedRowIndices);

   const selectedRowsData = selectedNodes.map((node) => node.data);
   const rowsToDelete = selectedRowsData.filter((row) => !row.isNewRow);

   if (!preventDefaultRowDeletion) {
      api.applyTransaction({
         remove: selectedRowsData,
      });

      const rowCount = api.getDisplayedRowCount();

      let rowToSelectIndex = null;
      if (maxIndex < rowCount) {
         rowToSelectIndex = maxIndex;
      } else if (minIndex > 0) {
         rowToSelectIndex = minIndex - 1;
      }

      if (rowToSelectIndex !== null) {
         const rowToSelectNode = api.getDisplayedRowAtIndex(rowToSelectIndex);
         if (rowToSelectNode) {
            api.ensureIndexVisible(rowToSelectIndex);
            rowToSelectNode.setSelected(true);
         }
      }
   }

   onDeleteChanges(
      preventDefaultRowDeletion ? selectedRowsData : rowsToDelete,
      gridApiRef.current
   );
}

export async function saveData(gridApiRef, onSaveChanges) {
   const editedData = [];
   const rowData = [];
   gridApiRef.current.forEachNode((node) => {
      if (node.data.isEdited || node.data.isNewRow)
         editedData.push({ ...node.data, rowIndex: node.rowIndex });

      rowData.push(node.data);
   });

   if (!editedData.length) {
      toastWarn(NOTIFICATION_MESSAGES.NO_CHANGES_TO_SAVE);
      return;
   }

   onSaveChanges(editedData, rowData, gridApiRef.current);
}

export async function pasteClipboardData(gridApiRef) {
   try {
      const clipboardData = await navigator.clipboard.readText();
      if (!clipboardData) {
         toastWarn(NOTIFICATION_MESSAGES.EMPTY_CLIPBOARD_DATA);
         return;
      }

      if (!validateClipboardInput(clipboardData)) {
         toastWarn(NOTIFICATION_MESSAGES.INVALID_CLIPBOARD_DATA);
         return;
      }

      appendNewRows(clipboardData, gridApiRef);
   } catch (err) {
      console.error('Failed to read clipboard contents:', err);
   }
}

const appendNewRows = (pastedText, gridApiRef) => {
   const lines = pastedText.split('\n').filter(Boolean);

   const newRows = lines.map((line) => {
      // todo(Renu): refactor this to work for all screens
      const [marketId = '', origin = '', destination = ''] = line.split('\t');

      return {
         marketId,
         origin,
         destination,
         isNewRow: true,
         id: createUniqueId(),
      };
   });

   const selectedNodes = gridApiRef?.current?.getSelectedNodes() || [];
   const newRowIndex = selectedNodes[selectedNodes.length - 1]?.rowIndex || 0;
   gridApiRef.current.applyTransaction({
      add: newRows,
      addIndex: newRowIndex + 1,
   });
};

const undoChanges = (gridApiRef) => {
   const rowsToRemove = [];
   const rowsToUndo = [];

   gridApiRef.current.forEachNode((node) => {
      if (node.data.isNewRow) {
         rowsToRemove.push(node);
      } else if (node.data.isEdited) {
         rowsToUndo.push({ ...node.data, isEdited: false });
      }
   });

   gridApiRef.current.applyTransaction({
      remove: rowsToRemove,
   });

   gridApiRef.current.applyTransaction({
      update: rowsToUndo,
   });
};

const cancelRecords = (gridApiRef, onCancel) => {
   const rowsToCancel = [];

   const selectedNodes = gridApiRef?.current?.getSelectedNodes() || [];

   selectedNodes.forEach((node) => {
      if (!node.data.isNewRow) {
         node.data.isEdited = true; // edits are to be made in the parent component using gridApi
         rowsToCancel.push(node);
      }
   });

   onCancel(rowsToCancel, gridApiRef.current);
};

export function getContextMenuItems(gridApiRef, contextMenuConfig = {}) {
   const {
      enableInsertNewRowWithData = false,
      insertNewRowWithDataText = '',
      onInsertNewRowWithData = () => {},
      enableInsertNewRow = false,
      enableInsertTemplate = false,
      enableDelete = false,
      enableRemoveSelectedRow = false,
      enableUndo = false,
      enableOpenNewTab = false,
      openNewTabText = '',
      enableDistributeData = false,
      distributeDataText = '',
      enableSave = false,
      enableCopy = false,
      enableCopyWithHeaders = false,
      enablePaste = false,
      enableExportData = true,
      onSaveChanges = () => {},
      onDeleteChanges = () => {},
      onRemoveSelectedRows = () => {},
      onOpenNewTab = () => {},
      onDistribute = () => {},
      enableCancel = false,
      onCancel = () => {},
      preventDefaultRowDeletion = false,
      onAddNewRow = () => {},
      onTemplate = () => {},
   } = contextMenuConfig;

   return [
      enableInsertNewRow && {
         name: 'Insert Row',
         shortcut: 'Ctrl+I',
         action: () => addRowAboveSelected(gridApiRef, onAddNewRow),
      },
      enableInsertTemplate && {
         name: 'Insert Template',
         action: () => addTemplateAboveSelected(gridApiRef, onTemplate),
      },
      enableInsertNewRowWithData && {
         name: insertNewRowWithDataText,
         shortcut: 'Ctrl+G',
         action: () => onInsertNewRowWithData(gridApiRef),
      },
      enableCancel && {
         name: 'Cancel',
         shortcut: 'Ctrl+X',
         action: () => cancelRecords(gridApiRef, onCancel),
      },
      enableUndo && {
         name: 'Undo',
         shortcut: 'Ctrl+Z',
         action: () => undoChanges(gridApiRef),
      },
      enableDelete && {
         name: 'Delete',
         shortcut: 'Ctrl+D',
         action: () =>
            deleteSelectedRows(
               gridApiRef,
               onDeleteChanges,
               preventDefaultRowDeletion
            ),
      },
      enableSave && {
         name: 'Save',
         shortcut: 'Ctrl+S',
         action: () => saveData(gridApiRef, onSaveChanges),
      },
      (enableCopy || enableCopyWithHeaders) && {
         name: 'Copy Options',
         subMenu: [
            enableCopy && 'copy',
            enableCopyWithHeaders && 'copyWithHeaders',
         ],
      },
      enableRemoveSelectedRow && {
         name: 'Remove Selected',
         shortcut: 'Ctrl+Q',
         action: () =>
            deleteSelectedRows(
               gridApiRef,
               onRemoveSelectedRows,
               preventDefaultRowDeletion
            ),
      },
      enableOpenNewTab && {
         name: openNewTabText,
         shortcut: 'Ctrl+R',
         action: () => selectedRows(gridApiRef, onOpenNewTab),
      },
      enableDistributeData && {
         name: distributeDataText,
         shortcut: 'Ctrl+B',
         action: () => selectedRows(gridApiRef, onDistribute),
      },
      enablePaste && {
         name: 'Paste',
         shortcut: 'Ctrl+V',
         action: () => pasteClipboardData(gridApiRef),
      },
      enableExportData && 'export',
   ];
}

export function createCellValueChangedHandler(
   onSelectionValueChanged,
   gridApiRef
) {
   return function onCellValueChanged(params) {
      const {
         column: { colId },
         newValue,
         oldValue,
      } = params;

      if (newValue === oldValue) return;
      const selectedNodes = gridApiRef.current?.getSelectedNodes() || [];

      selectedNodes.forEach((node) => {
         node.setDataValue(colId, newValue);
         node.data.isEdited = true;
      });

      onSelectionValueChanged?.(colId, params.data.isEdited);
   };
}

export const handleKeyboardShortcuts = (
   event,
   gridApiRef,
   contextMenuConfig = {}
) => {
   const api = gridApiRef.current;
   if (!api) return;

   const { ctrlKey } = event;
   const {
      enableInsertNewRowWithData = false,
      onInsertNewRowWithData = () => {},
      enableInsertNewRow = false,
      enableInsertTemplate = false,
      enableDelete = false,
      enableSave = false,
      enableRemoveSelectedRow = false,
      enableUndo = false,
      enablePaste = false,
      enableOpenNewTab = false,
      enableDistributeData = false,
      onSaveChanges = () => {},
      onDeleteChanges = () => {},
      onRemoveSelectedRows = () => {},
      onOpenNewTab = () => {},
      onDistribute = () => {},
      enableCancel = false,
      onCancel = () => {},
      preventDefaultRowDeletion,
   } = contextMenuConfig;

   if (ctrlKey) {
      if (enableInsertNewRow && (event.key === 'i' || event.key === 'I')) {
         event.preventDefault();
         addRowAboveSelected(gridApiRef);
      }

      if (enableInsertTemplate && (event.key === 't' || event.key === 'T')) {
         event.preventDefault();
         addTemplateAboveSelected(gridApiRef);
      }

      if (enableDelete && (event.key === 'd' || event.key === 'D')) {
         event.preventDefault();
         deleteSelectedRows(
            gridApiRef,
            onDeleteChanges,
            preventDefaultRowDeletion
         );
      }

      if (enableSave && (event.key === 's' || event.key === 'S')) {
         event.preventDefault();
         saveData(gridApiRef, onSaveChanges);
      }

      if (enablePaste && (event.key === 'v' || event.key === 'V')) {
         event.preventDefault();
         pasteClipboardData(gridApiRef);
      }

      if (enableUndo && (event.key === 'z' || event.key === 'Z')) {
         event.preventDefault();
         undoChanges(gridApiRef);
      }

      if (enableCancel && (event.key === 'x' || event.key === 'X')) {
         event.preventDefault();
         cancelRecords(gridApiRef, onCancel);
      }

      if (
         enableInsertNewRowWithData &&
         (event.key === 'g' || event.key === 'G')
      ) {
         event.preventDefault();
         onInsertNewRowWithData(gridApiRef);
      }

      if (enableRemoveSelectedRow && (event.key === 'q' || event.key === 'Q')) {
         event.preventDefault();
         selectedRows(gridApiRef, onRemoveSelectedRows);
      }

      if (enableOpenNewTab && (event.key === 'r' || event.key === 'R')) {
         event.preventDefault();
         selectedRows(gridApiRef, onOpenNewTab);
      }

      if (enableDistributeData && (event.key === 'b' || event.key === 'B')) {
         event.preventDefault();
         selectedRows(gridApiRef, onDistribute);
      }
   }
};

export const createCellEditStartHandler =
   () =>
   ({ api }) => {
      const selectedNodes = api.getSelectedNodes() || [];
      selectedNodes.forEach((node) => {
         if (!node.data.originalData)
            node.data.originalData = JSON.parse(JSON.stringify(node.data));
      });
   };

async function selectedRows(gridApiRef, onRemoveSelectedRows) {
   if (!gridApiRef || !gridApiRef.current) {
      console.error('Grid API reference is not provided or invalid.');
      return;
   }

   const api = gridApiRef.current;
   const selectedNodes = api.getSelectedNodes();

   if (selectedNodes.length === 0) {
      toastWarn(NOTIFICATION_MESSAGES.ROW_NOT_SELECTED);
      return;
   }

   const selectedRowsData = selectedNodes.map((node) => node.data);
   onRemoveSelectedRows(selectedRowsData);
}
